import { Injectable, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { AlarisProfileService, CustomValidators, DateFormatPipe } from '@campaign-portal/components-library';
import { FileInfo } from '@campaign-portal/namespace/common/fileInfo';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import {
	ChannelType,
	DecisionMode,
	FallbackTriggerType,
	MessagePurpose,
	MessageSplitMode,
	PreModerationEventStatus,
	WhatsappButtonType,
	WhatsappHeaderType,
	WhatsappTemplateCategory,
	WhatsappTemplateType,
} from '@campaign-portal/namespace/common/enums';
import {
	Campaign,
	CampaignChannelSettings,
	CampaignRepeatType,
	CampaignStatus,
} from '@campaign-portal/namespace/entities/campaigns/specs';
import { FileParsedColumn } from '@campaign-portal/namespace/entities/files/specs';
import { CPCustomValidators } from '@shared/validators';
import { CP_PERMISSIONS, ShortLinkChannelsMap } from '@shared/types';
import {
	CampaignChannelSettingsControls,
	CampaignControls,
	CampaignInfoControls,
	CampaignMessageControls,
	CampaignNativeControls,
	CampaignOmniSettingsControls,
	CampaignRepeatRuleControls,
	CampaignSettingsControls,
	CampaignSetupControls,
	CampaignSmsSettingsControls,
	CampaignStatisticsControls,
	CampaignViberSettingsControls,
} from '../shared/types/form-types';
import { ContactGroupsService, MessageTemplatesService, UrlShortenerUtilsService } from '@shared/services';
import { CampaignRecipientsType } from './campaign-recipients/campaign-recipients.component';
import { TemplateWhatsappSettingsControls } from '../message-templates/templates/update-template/interfaces/message-template-form.interfaces';
import {
	SmsTemplateSettings,
	ViberTemplateSettings,
	WhatsappButton,
	WhatsappTemplateSettings,
} from '@campaign-portal/namespace/entities/templates/specs';
import { CampaignUtilsService } from '@shared/services/campaign-utils.service';
import { DEFAULT_TTL_MINUTES } from '@shared/constants';

const MIN_TTL_MINUTES = 15;
const MAX_TTL_MINUTES = 1440;
const TIMEZONE_OFFSET_DIVISOR = 60;
const MAX_PHONE_NUMBER_AMOUNT = 1000;
export const CONTACTS_SPLIT_PATTERN = /[\s;,]+/g;

@Injectable({
	providedIn: 'root',
})
export class CampaignWizardService {
	totalRecipients = signal(0);
	forbiddenFlashWarning = false;
	readonly shortLinkChannelsMap$ = new BehaviorSubject<ShortLinkChannelsMap>(new Map());

	constructor(
		private readonly contactGroupsService: ContactGroupsService,
		private readonly templatesService: MessageTemplatesService,
		private readonly campaignUtils: CampaignUtilsService,
		private readonly shortenerUtils: UrlShortenerUtilsService,
		private readonly profile: AlarisProfileService,
		private readonly translate: TranslateService,
		private readonly dateFormatPipe: DateFormatPipe
	) {}

	_form!: FormGroup<CampaignControls>;

	get form(): FormGroup<CampaignControls> {
		return this._form;
	}

	set form(form: FormGroup<CampaignControls>) {
		this._form = form;
	}

	get infoControls(): CampaignInfoControls {
		return this._form.controls.info.controls;
	}

	get setupControls(): CampaignSetupControls {
		return this._form.controls.setup.controls;
	}

	get settingsControls(): CampaignSettingsControls {
		return this._form.controls.settings.controls;
	}

	get repeatRuleControls(): CampaignRepeatRuleControls | undefined {
		return this._form.controls.settings.controls.repeatRule?.controls;
	}

	get statisticsControls(): CampaignStatisticsControls {
		return this._form.controls.statistics.controls;
	}

	get contactsControls(): FormGroup<{
		file: FormControl<FileInfo | undefined>;
		plain: FormGroup<{
			text: FormControl<string>;
			code: FormControl<number | null>;
		}>;
		groups: FormControl<Id<exist>[] | undefined | null>;
	}> {
		return this._form.controls.setup.controls.native.controls.contacts;
	}

	get channelsFormArray(): FormArray<FormGroup<CampaignChannelSettingsControls>> {
		return this._form.controls.setup.controls.channels;
	}

	get channelsArray(): FormGroup<CampaignChannelSettingsControls>[] {
		return this._form.controls.setup.controls.channels.controls;
	}

	get contactsFile(): FormControl<FileInfo | undefined> {
		return this._form.controls.setup.controls.native.controls.contacts.controls.file;
	}

	get contactsPlain(): FormGroup<{ text: FormControl<string>; code: FormControl<number | null> }> {
		return this._form.controls.setup.controls.native.controls.contacts.controls.plain;
	}

	get contactGroups(): FormControl<Id<exist>[] | undefined | null> {
		return this._form.controls.setup.controls.native.controls.contacts.controls.groups;
	}

	get hasPreModeratedChannels(): boolean {
		const selectedChannelsSettings = this.channelsArray.map((fg) => fg.value);
		return selectedChannelsSettings.some((settings) => {
			const isPreModerated = this.campaignUtils.isPreModeratedChannel(settings.channelType);
			const templateId = settings.message?.templateID;
			const isConfirmedTemplateUsed = Boolean(
				templateId && this.templatesService.map.get(templateId)?.status === PreModerationEventStatus.CONFIRMED
			);
			return isPreModerated && !isConfirmedTemplateUsed;
		});
	}

	get isOnlyIMChannelsInCampaign(): boolean {
		return !this.findChannel(ChannelType.SMS);
	}

	findChannel(channelType: ChannelType): FormGroup<CampaignChannelSettingsControls> | undefined {
		return this._form.controls.setup.controls.channels.controls.find((control) => {
			return control.controls.channelType.value === channelType;
		});
	}

	create(type: 'file' | 'native' = 'native', campaign?: Campaign): FormGroup<CampaignControls> {
		this._form = this.initializeForm(campaign);
		if (type === 'file') {
			this.disableNativeSetup();
		} else {
			this.disableFileSetup();
		}
		return this._form;
	}

	reset(): void {
		this._form = this.create();
		this.totalRecipients.set(0);
		this.forbiddenFlashWarning = false;
		this.shortLinkChannelsMap$.next(new Map());
	}

	prepare(type: 'file' | 'native' = 'native'): Campaign<null> {
		const baseCampaign: Campaign<null> = this.initializeBaseCampaign();

		let campaign: Campaign<null>;

		if (type === 'file') {
			campaign = this.prepareFileCampaign(baseCampaign);
		} else {
			campaign = this.prepareNativeCampaign(baseCampaign);
		}

		return campaign;
	}

	repeat(campaign: Campaign): void {
		if (campaign.id) {
			campaign.info.name = this.translate.instant('actions.repeat') + ' ' + campaign.info.name;
		}
		if (campaign.settings.isFlashed && !this.profile.user.details.isFlashed) {
			this.forbiddenFlashWarning = true;
			campaign.settings.isFlashed = false;
		}
		if (campaign.settings.isMo && !this.profile.user.details.isMo) {
			campaign.settings.isMo = false;
		}
		this.totalRecipients.set(campaign.statistics?.total ?? 0);
		this.create('native', campaign);
		this.enableContactsControls(this.determineRecipientType());
		this.checkShortLinks();
	}

	checkShortLinks(): void {
		if (!this.profile.allowed([CP_PERMISSIONS.URL_SHORTENER_E])) {
			return;
		}
		const shortLinkChannelsMap: ShortLinkChannelsMap = new Map();
		this.channelsArray.forEach((ch) => {
			const channel = ch.getRawValue();
			switch (channel.channelType) {
				case ChannelType.SMS:
				case ChannelType.VIBER:
				case ChannelType.TELEGRAM: {
					if (!channel.message) {
						return;
					}
					const message =
						channel.message.text ??
						this.templatesService.map.get(channel.message.templateID ?? -1)?.messageTemplate ??
						'';
					shortLinkChannelsMap.set(channel.channelType, this.shortenerUtils.extractShortLink(message));
					break;
				}
				case ChannelType.WHATSAPP: {
					const shortUrlButton = channel.omni?.whatsappSettings?.buttons.find((button) => button.shortUrl);
					const shortLinkEntry = this.shortenerUtils.extractShortLink(shortUrlButton?.shortUrl || '');
					shortLinkChannelsMap.set(channel.channelType, shortLinkEntry);
				}
			}
		});
		this.shortLinkChannelsMap$.next(shortLinkChannelsMap);
	}

	determineRecipientType(): CampaignRecipientsType {
		const { plain, file } = this.contactsControls.value;

		if (plain?.text) {
			return CampaignRecipientsType.MANUALLY;
		}
		if (file) {
			return CampaignRecipientsType.FILE;
		}
		if (this.profile.allowed([CP_PERMISSIONS.CONTACTS_R])) {
			return CampaignRecipientsType.GROUP;
		}
		return CampaignRecipientsType.MANUALLY;
	}

	enableContactsControls(recipientsType: CampaignRecipientsType): void {
		this.contactGroups.disable();
		this.contactsPlain.disable();
		this.contactsFile.disable();

		switch (recipientsType) {
			case CampaignRecipientsType.GROUP:
				this.contactGroups.enable();
				break;
			case CampaignRecipientsType.MANUALLY:
				this.contactsPlain.enable();
				break;
			case CampaignRecipientsType.FILE:
				this.contactsFile.enable();
				break;
		}
	}

	createSmsSettingsGroup(settings?: SmsTemplateSettings | null): FormGroup<CampaignSmsSettingsControls> {
		return new FormGroup<CampaignSmsSettingsControls>({
			messagePurpose: new FormControl<MessagePurpose | null>(settings?.messagePurpose ?? MessagePurpose.TRANSACTION, {
				nonNullable: true,
			}),
		});
	}

	createWhatsappSettingsGroup(settings?: WhatsappTemplateSettings): FormGroup<TemplateWhatsappSettingsControls> {
		return new FormGroup<TemplateWhatsappSettingsControls>({
			category: new FormControl<WhatsappTemplateCategory>(settings?.category || WhatsappTemplateCategory.MARKETING, {
				nonNullable: true,
			}),
			language: new FormControl<string | null>(settings?.language || null),
			type: new FormControl<WhatsappTemplateType | null>(settings?.type || null),
			header: this.createWhatsappHeaderGroup(settings?.header),
			footer: new FormControl<string | undefined>(settings?.footer, { nonNullable: true }),
			authentication: this.createWhatsappAuthGroup(settings?.authentication),
			buttons: new FormArray(settings?.buttons?.map((button) => this.createWhatsappButtonGroup(button)) ?? []),
		});
	}

	createRepeatRuleGroup(): FormGroup<CampaignRepeatRuleControls> {
		const timezoneOffset = -new Date().getTimezoneOffset() / TIMEZONE_OFFSET_DIVISOR;
		return new FormGroup<CampaignRepeatRuleControls>({
			id: new FormControl<Id>(null),
			repeatType: new FormControl(CampaignRepeatType.NONE, { nonNullable: true }),
			userTimezone: new FormControl(timezoneOffset, { nonNullable: true }),
			startDate: new FormControl(new Date().toISOString(), { nonNullable: true }),
			endDate: new FormControl(null),
			repeatInterval: new FormControl(null),
			repeatWeekdays: new FormControl([], { nonNullable: true }),
			repeatMonthlyMode: new FormControl(null),
			repeatContactVariable: new FormControl(null),
		});
	}

	private createWhatsappHeaderGroup(header?: WhatsappTemplateSettings['header']): FormGroup {
		return new FormGroup({
			type: new FormControl<WhatsappHeaderType>(header?.type || WhatsappHeaderType.TEXT, { nonNullable: true }),
			value: new FormControl<string>(header?.value || '', { nonNullable: true }),
		});
	}

	private createWhatsappAuthGroup(auth?: WhatsappTemplateSettings['authentication']): FormGroup {
		return new FormGroup({
			code: new FormControl<string | undefined>(auth?.code, { nonNullable: true }),
			codeExpirationDate: new FormControl<string | undefined>(auth?.codeExpirationDate, { nonNullable: true }),
		});
	}

	private createWhatsappButtonGroup(button: WhatsappButton): FormGroup {
		return new FormGroup({
			type: new FormControl<WhatsappButtonType | null>(button?.type ?? null, Validators.required),
			name: new FormControl<string | undefined>(button?.name, { nonNullable: true }),
			phone: new FormControl<string | undefined>(button?.phone, { nonNullable: true }),
			url: new FormControl<string | undefined>(button?.url, { nonNullable: true }),
			shortUrl: new FormControl<string | undefined>(button?.shortUrl, { nonNullable: true }),
			code: new FormControl<string | undefined>(button?.code, { nonNullable: true }),
		});
	}

	private initializeForm(campaign?: Campaign): FormGroup<CampaignControls> {
		return new FormGroup<CampaignControls>({
			id: new FormControl<Id>(null),
			info: this.createInfoGroup(campaign),
			setup: new FormGroup({
				native: this.createNativeSetup(campaign),
				file: this.createFileSetup(),
				channels: this.createChannelsArray(campaign),
			}),
			settings: this.createSettingsGroup(campaign),
			statistics: this.createStatisticsGroup(),
		});
	}

	private prepareFileCampaign(baseCampaign: Campaign<null>): Campaign<null> {
		Object.assign(baseCampaign.setup, { file: {} });
		return Object.assign(baseCampaign, structuredClone(this.form.value));
	}

	private prepareNativeCampaign(baseCampaign: Campaign<null>): Campaign<null> {
		Object.assign(baseCampaign.setup, { native: { contacts: [] } });

		const campaign = Object.assign(baseCampaign, structuredClone(this.form.value));

		if (this.setupControls.native.controls.contacts.value.groups === null && campaign.setup.native) {
			campaign.setup.native.contacts.groups = this.contactGroupsService.list.map((cg) => cg.id);
		}
		if (this.setupControls.native.controls.contacts.value.plain && campaign.setup.native) {
			campaign.setup.native.contacts.plain = {
				code: this.contactsPlain.controls.code.value,
				text: this.contactsPlain.controls.text.value.replaceAll(CONTACTS_SPLIT_PATTERN, ','),
			};
		}
		return campaign;
	}

	private initializeBaseCampaign(): Campaign<null> {
		return {
			id: null,
			info: {
				id: null,
				scheduled: '',
				status: CampaignStatus.SCHEDULED,
			},
			setup: {
				channels: [],
			},
			settings: {},
		};
	}

	private createInfoGroup(campaign?: Campaign): FormGroup<CampaignInfoControls> {
		const defaultName = `${this.translate.instant('campaigns.campaignName')} ${this.dateFormatPipe.transform(new Date())}`;

		return new FormGroup<CampaignInfoControls>({
			id: new FormControl<Id>(null),
			name: new FormControl<string>(campaign?.info.name ?? defaultName, { nonNullable: true }),
			description: new FormControl<string>(campaign?.info.description ?? '', { nonNullable: true }),
			photo: new FormControl<string>('', { nonNullable: true }),
			scheduled: new FormControl<string>(new Date().toISOString(), {
				nonNullable: true,
				validators: [Validators.required],
			}),
			status: new FormControl<CampaignStatus>(CampaignStatus.SCHEDULED, { nonNullable: true }),
		});
	}

	private createNativeSetup(campaign?: Campaign): FormGroup<CampaignNativeControls> {
		const contacts = campaign?.setup.native?.contacts;
		return new FormGroup<CampaignNativeControls>({
			contacts: new FormGroup(
				{
					file: this.createContactsFileControl(contacts?.file),
					plain: this.createContactsPlainGroup(contacts?.plain),
					groups: this.createContactsGroupsControl(contacts?.groups),
				},
				[Validators.required]
			),
		});
	}

	private createContactsFileControl(file?: FileInfo): FormControl<FileInfo | undefined> {
		return new FormControl<FileInfo | undefined>({ value: file, disabled: !file }, { nonNullable: true });
	}

	private createContactsPlainGroup(plain?: { text?: string; code?: number | null }): FormGroup {
		return new FormGroup({
			text: new FormControl<string>(
				{ value: plain?.text ?? '', disabled: !plain?.text },
				{
					nonNullable: true,
					validators: [CPCustomValidators.recipientsLimitAndPattern(MAX_PHONE_NUMBER_AMOUNT, CONTACTS_SPLIT_PATTERN)],
				}
			),
			code: new FormControl<number | null>({ value: plain?.code ?? null, disabled: !plain?.code }),
		});
	}

	private createContactsGroupsControl(groups?: Id<exist>[] | null): FormControl<Id<exist>[] | undefined | null> {
		const hasGroups = groups && groups.length > 0;
		return new FormControl<Id<exist>[] | undefined | null>(
			{ value: groups ?? [], disabled: !hasGroups },
			{ nonNullable: true, validators: [CustomValidators.requiredArrayOrNull] }
		);
	}

	private createFileSetup(): FormGroup<{
		file: FormControl<FileInfo | null>;
		columns: FormControl<FileParsedColumn[]>;
	}> {
		return new FormGroup({
			file: new FormControl<FileInfo | null>(null, { validators: Validators.required }),
			columns: new FormControl<FileParsedColumn[]>([], { nonNullable: true }),
		});
	}

	private createChannelsArray(campaign?: Campaign): FormArray<FormGroup<CampaignChannelSettingsControls>> {
		return new FormArray<FormGroup<CampaignChannelSettingsControls>>(
			campaign?.setup.channels?.map((chSetup) => this.createChannelGroup(chSetup)) ?? [],
			[Validators.required]
		);
	}

	private createChannelGroup(chSetup: CampaignChannelSettings): FormGroup<CampaignChannelSettingsControls> {
		const isTextOptional = Boolean(chSetup.omni?.viberSettings?.imageUrl);
		const formGroup = new FormGroup<CampaignChannelSettingsControls>({
			sender: new FormControl<Id<exist> | null>(chSetup.sender, Validators.required),
			channelType: new FormControl<ChannelType>(chSetup.channelType, { nonNullable: true }),
			message: chSetup.message
				? new FormGroup<CampaignMessageControls>({
						text: new FormControl<string | undefined>(
							{
								value: chSetup.message.text,
								disabled: chSetup.omni?.whatsappSettings?.category === WhatsappTemplateCategory.AUTHENTICATION,
							},
							{
								nonNullable: true,
								validators: isTextOptional ? [] : Validators.required,
							}
						),
						templateID: new FormControl<Id<exist> | null>(chSetup.message.templateID ?? null),
					})
				: undefined,
		});
		if (chSetup.omni) {
			formGroup.addControl('omni', this.createOmniSettingsGroup(chSetup));
		}
		return formGroup;
	}

	private createOmniSettingsGroup(chSetup: CampaignChannelSettings): FormGroup<CampaignOmniSettingsControls> {
		const form = new FormGroup<CampaignOmniSettingsControls>({
			billingMode: new FormControl<DecisionMode>(chSetup.omni?.billingMode ?? DecisionMode.BY_SUBMITTED, {
				nonNullable: true,
			}),
			ttl: new FormControl<number>(chSetup.omni?.ttl ?? DEFAULT_TTL_MINUTES, {
				nonNullable: true,
				validators: [Validators.required, Validators.min(MIN_TTL_MINUTES), Validators.max(MAX_TTL_MINUTES)],
			}),
			fallbackTriggerType: new FormControl<FallbackTriggerType>(
				chSetup.omni?.fallbackTriggerType ?? FallbackTriggerType.NOT_DELIVERED,
				{ nonNullable: true }
			),
		});
		if (chSetup.channelType === ChannelType.SMS) {
			form.addControl('smsSettings', this.createSmsSettingsGroup(chSetup.omni?.smsSettings));
		}

		if (chSetup.omni?.viberSettings) {
			form.addControl('viberSettings', this.createViberSettingsGroup(chSetup.omni.viberSettings));
		}
		if (chSetup.omni?.whatsappSettings) {
			form.addControl('whatsappSettings', this.createWhatsappSettingsGroup(chSetup.omni.whatsappSettings));
		}
		return form;
	}

	private createViberSettingsGroup(settings: ViberTemplateSettings): FormGroup<CampaignViberSettingsControls> {
		const { messagePurpose, buttonActionUrl, imageUrl, buttonCaption } = settings;
		const isPromotional = !!imageUrl || !!(buttonActionUrl && buttonCaption);
		const purpose = messagePurpose || (isPromotional ? MessagePurpose.PROMOTION : MessagePurpose.TRANSACTION);

		return new FormGroup<CampaignViberSettingsControls>(
			{
				buttonActionUrl: new FormControl<string>(settings.buttonActionUrl ?? '', { nonNullable: true }),
				imageUrl: new FormControl<string>(settings.imageUrl ?? '', { nonNullable: true }),
				buttonCaption: new FormControl<string>(settings.buttonCaption ?? '', { nonNullable: true }),
				messagePurpose: new FormControl<MessagePurpose>(purpose, { nonNullable: true }),
			},
			CPCustomValidators.viberButtonPair()
		);
	}

	private createSettingsGroup(campaign?: Campaign): FormGroup<CampaignSettingsControls> {
		return new FormGroup<CampaignSettingsControls>({
			failureAlertThreshold: new FormControl<number>(
				{
					value: campaign?.settings.failureAlertThreshold ?? 100,
					disabled: !campaign?.settings.failureAlertThreshold,
				},
				{ nonNullable: true }
			),
			emailReportAddresses: new FormControl<string>(
				{
					value: campaign?.settings.emailReportAddresses ?? '',
					disabled: !campaign?.settings.emailReportAddresses,
				},
				{ nonNullable: true, validators: [Validators.required] }
			),
			messageSplitMode: new FormControl<MessageSplitMode>(
				campaign?.settings.messageSplitMode ?? MessageSplitMode.SPLIT,
				{ nonNullable: true }
			),
			isFlashed: new FormControl<boolean>(campaign?.settings.isFlashed || false, { nonNullable: true }),
			isMo: new FormControl<boolean>(campaign?.settings.isMo || false, { nonNullable: true }),
		});
	}

	private createStatisticsGroup(): FormGroup<CampaignStatisticsControls> {
		return new FormGroup({
			preCalculatedCost: new FormGroup({
				min: new FormControl<number>(0, { nonNullable: true }),
				max: new FormControl<number>(0, { nonNullable: true }),
			}),
		});
	}

	private disableNativeSetup(): void {
		this._form.controls.setup.controls.native.disable();
		this._form.controls.setup.controls.file.enable();
	}

	private disableFileSetup(): void {
		this._form.controls.setup.controls.file.disable();
		this._form.controls.setup.controls.native.enable();
	}
}
